import type { GraphQLErrorExtensions } from 'graphql';
import { ClientError as GraphQLError } from 'graphql-request';
import { i18n } from '@/i18n';
import { ErrorCodes } from '@/types/gql.generated';

export { GraphQLError };
export type QueryError = GraphQLError | Error;
export type ErrorExtensionsData = Record<string, unknown>;

const getDefaultErrorCodeMessage = (code: ErrorCodes): string | undefined => {
  const t = i18n.getFixedT('en', 'common', 'default_error_code_messages');

  const defaultErrorCodeMessages: { [key in ErrorCodes]?: string } = {
    [ErrorCodes.EntryScheduledMessageLimitReached]: t(
      'EntryScheduledMessageLimitReached'
    ),
    [ErrorCodes.EntryMessageInPast]: t('EntryMessageInPast'),
    [ErrorCodes.LabelExists]: t('LabelExists'),
  };

  return defaultErrorCodeMessages[code];
};

export const getGQLErrorCode = (error: GraphQLError): ErrorCodes => {
  // `?.[0]?.` rather than `?.[0].` — the optional chain guards `errors` being
  // absent, but an empty `errors` array yields `undefined[0]` and the property
  // read then throws. Reachable from the query/mutation cache error handlers,
  // where a throw takes out the handler itself.
  return error.response.errors?.[0]?.extensions?.code as ErrorCodes;
};

export const getGQLErrorMessage = (error: GraphQLError): string | undefined => {
  return error.response.errors?.[0]?.message;
};

/**
 * Is the `error` input a GQL error vs a network or some other type of error?
 */
export const isGQLError = (error: unknown): error is GraphQLError => {
  return error instanceof GraphQLError;
};

/**
 * If the `error` input a GQL error AND an error with a specific error code
 * that we've defined?
 */
export const isGQLErrorOfType = (
  error: unknown,
  type: keyof typeof ErrorCodes
): error is GraphQLError => {
  if (isGQLError(error)) {
    return getGQLErrorCode(error) === ErrorCodes[type];
  }
  return false;
};

const getGenericErrorMessage = (): string =>
  i18n.t('common:error_boundary.message');

/**
 * `ClientError` builds its `message` as
 * `${extractMessage(response)}: ${JSON.stringify({ response, request })}`, and
 * `request` carries the operation variables — for the auth mutations that's the
 * user's plaintext password. Its `message` must never reach the UI.
 *
 * Checked structurally rather than with `instanceof` so a `ClientError` from a
 * second copy of graphql-request can't slip past.
 */
const isClientErrorLike = (error: unknown): boolean =>
  isGQLError(error) ||
  (typeof error === 'object' &&
    error !== null &&
    'request' in error &&
    'response' in error);

/**
 * graphql-js inlines the offending value into variable coercion errors
 * (`Variable "$password" got invalid value "hunter2"; ...`), so a server-authored
 * message is only safe to render once those are ruled out. They're internal
 * diagnostics rather than user-facing copy in any case.
 *
 * Matched anywhere in the string, not just at the start, since a wrapping layer
 * can prefix the diagnostic (`Bad request: Variable "$password" got...`).
 */
const isDisplayableServerMessage = (message: string): boolean =>
  !message.includes('Variable "$');

/**
 * Extract an error message from a react-query error
 */
export const getErrorMessage = (error: QueryError): string => {
  if (!isGQLError(error)) {
    return isClientErrorLike(error) ? getGenericErrorMessage() : error.message;
  }

  const code = getGQLErrorCode(error);
  let message = getGQLErrorMessage(error);

  // server defaults to using the error code as the message when
  // a human readable message isn't provided
  if (code && (!message || message === code)) {
    message = getDefaultErrorCodeMessage(code);
  }
  if (!message || !isDisplayableServerMessage(message)) {
    message = getGenericErrorMessage();
  }

  return message;
};

/**
 * The server-authored message, or `undefined` when it isn't safe to forward.
 *
 * Safe to send to Sentry, unlike `error.message` — `ClientError` builds that by
 * serializing the whole request, variables included. This reads only
 * `response.errors[0].message` and drops variable-coercion diagnostics, which
 * graphql-js writes with the rejected value inlined.
 */
export const getReportableServerMessage = (
  error: GraphQLError
): string | undefined => {
  const message = getGQLErrorMessage(error);
  return message && isDisplayableServerMessage(message) ? message : undefined;
};

const isGQLErrorExtensions = (
  extensions: unknown
): extensions is GraphQLErrorExtensions => {
  return typeof extensions === 'object' && extensions !== null;
};

const isGQLErrorExtensionsData = (
  extensionsData: unknown
): extensionsData is ErrorExtensionsData => {
  return (
    !!extensionsData &&
    typeof extensionsData === 'object' &&
    extensionsData !== null
  );
};

/**
 * Return custom data associated with a GraphQL error (if exists).
 */
export const getGQLErrorExtensionData = (
  error: GraphQLError
): ErrorExtensionsData | undefined => {
  const extensions = error.response?.errors?.[0].extensions;

  if (
    isGQLErrorExtensions(extensions) &&
    isGQLErrorExtensionsData(extensions.data)
  ) {
    return extensions.data;
  }

  return undefined;
};

/**
 * Generic way to wrap an error with one of our existing error codes.
 * This is commonly used when fetching a job that has failed (network request is 200)
 * but the job contains an error code in its response.
 */
export class ErrorWithCode extends Error {
  public code: ErrorCodes;

  constructor(message: string, code?: ErrorCodes | null) {
    super(message);
    this.code = code || ErrorCodes.UnknownError;
  }
}

export const isErrorWithCode = (error: unknown): error is ErrorWithCode => {
  return error instanceof ErrorWithCode;
};
